import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Checkbox,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import GetAppIcon from '@mui/icons-material/GetApp';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TenantFileUploadDialog from './TenantFileUploadDialog';
import TenantEditDialog from './TenantEditDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { styled } from '@mui/material/styles';

interface TenantFile {
  id: string;
  fileUrl: string;
  active: boolean;
}

interface Tenant {
  id: string;
  fullName: string;
  phoneNumbers: string[];
  emails: string[];
  nationality: string | null;
  identificationType: string;
  identificationTypeId: string;
  active: boolean;
  files?: TenantFile[];
}

interface Unit {
  id: string;
  number: string;
  rooms: number;
  bathrooms: number;
  floor: number;
  areaM2: number;
  propertyId: string;
  property: any;
  unitFeeConfigs: any[];
  appliances: any[];
  active: boolean;
}

interface Lease {
  id: string;
  createdDate: string;
  startDate: string;
  endDate: string;
  status: string;
  refNumber: string;
  leaseType: string;
  leaseTypeId: string;
  currency: string;
  currencyId: string;
  unit: Unit;
  tenant: any;
  fees: Array<{
    id: string;
    value: number;
    type: string;
    currency: string;
    currencyId: string;
    billingCycle: string;
    notes: string | null;
    active: boolean;
  }>;
  active: boolean;
}

// Status mapping for string-based status
const LeaseStatus: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }> = {
  'Draft': { label: 'Draft', color: 'warning' },
  'Active': { label: 'Active', color: 'success' },
  'Expired': { label: 'Expired', color: 'error' },
  'Terminated': { label: 'Terminated', color: 'default' }
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8]
  }
}));

const LeaseHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: `${theme.spacing(1.5)} ${theme.spacing(1.5)} 0 0`
}));

interface TenantDetailsDialogProps {
  open: boolean;
  tenantId: string | null;
  onClose: () => void;
  onRefresh?: () => void;
}

const TenantDetailsDialog: React.FC<TenantDetailsDialogProps> = ({
  open,
  tenantId,
  onClose,
  onRefresh
}) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [deletingFiles, setDeletingFiles] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [leasesLoading, setLeasesLoading] = useState(false);
  const dispatch = useAppDispatch();

  // Fetch tenant leases
  const fetchTenantLeases = async (tenantId: string) => {
    setLeasesLoading(true);
    try {
      const response = await DataService.get(`api/Lease/tenant/${tenantId}`);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Tenant Leases Response:', responseData);

        let leasesList: Lease[] = [];

        if (responseData.success && responseData.data) {
          leasesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          leasesList = responseData;
        }

        setLeases(leasesList);
      } else {
        throw new Error('Failed to fetch tenant leases');
      }
    } catch (error) {
      console.error('Error fetching tenant leases:', error);
      // Don't show error alert for leases, just log it
      setLeases([]);
    } finally {
      setLeasesLoading(false);
    }
  };

  // Fetch tenant details when dialog opens
  useEffect(() => {
    const fetchTenant = async () => {
      if (!tenantId || !open) return;

      setLoading(true);
      setError(null);
      setSelectedFiles([]); // Clear selected files when opening dialog

      try {
        const response = await DataService.get(`api/Tenant/get/${tenantId}`);

        if (response.ok) {
          const responseData = await response.json();
          console.log('Individual tenant API Response:', responseData);

          let tenantData: Tenant;
          if (responseData.success && responseData.data) {
            tenantData = responseData.data;
          } else if (responseData.id) {
            tenantData = responseData;
          } else {
            throw new Error('Invalid response structure');
          }

          setTenant(tenantData);

          // Fetch tenant leases
          await fetchTenantLeases(tenantId);
        } else {
          throw new Error(`Failed to fetch tenant: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching tenant:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load tenant details. Please try again.';
        setError(errorMessage);
        dispatch(showAlert({
          message: errorMessage,
          type: 'error'
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [tenantId, open, dispatch]);

  if (!tenant && !loading) return null;

  // Generate initials from full name
  const initials = tenant?.fullName?.split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2) || '';

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get days remaining helper
  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get status info helper
  const getStatusInfo = (status: string) => {
    return LeaseStatus[status] || { label: status || 'Unknown', color: 'default' as const };
  };

  // Extract file name from URL
  const getFileNameFromUrl = (fileUrl: string): string => {
    try {
      const url = new URL(fileUrl);
      const pathname = url.pathname;
      const fileName = pathname.split('/').pop() || 'unknown-file';
      return decodeURIComponent(fileName);
    } catch {
      return fileUrl.split('/').pop() || 'unknown-file';
    }
  };

  // Get file extension from URL
  const getFileExtension = (fileUrl: string): string => {
    const fileName = getFileNameFromUrl(fileUrl);
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  // Get appropriate file icon based on file URL/extension
  const getFileIcon = (fileUrl: string) => {
    const extension = getFileExtension(fileUrl);
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return <ImageIcon color="primary" />;
    } else if (extension === 'pdf') {
      return <PictureAsPdfIcon color="error" />;
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return <DescriptionIcon color="info" />;
    } else {
      return <AttachFileIcon color="action" />;
    }
  };

  // Handle individual file download
  const handleFileDownload = (file: TenantFile) => {
    const fileName = getFileNameFromUrl(file.fileUrl);
    const link = document.createElement('a');
    link.href = file.fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle download all files
  const handleDownloadAllFiles = async () => {
    if (!tenant?.files || tenant.files.length === 0) return;

    setDownloadingAll(true);
    try {
      for (const file of tenant.files.filter(f => f.active)) {
        await new Promise(resolve => {
          const fileName = getFileNameFromUrl(file.fileUrl);
          const link = document.createElement('a');
          link.href = file.fileUrl;
          link.download = fileName;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(resolve, 500); // Small delay between downloads
        });
      }

      dispatch(showAlert({
        message: `Successfully initiated download for ${tenant.files.filter(f => f.active).length} files`,
        type: 'success'
      }));
    } catch (error) {
      console.error('Error downloading files:', error);
      dispatch(showAlert({
        message: 'Some files may not have downloaded successfully',
        type: 'warning'
      }));
    } finally {
      setDownloadingAll(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (fileId: string, checked: boolean) => {
    setSelectedFiles(prev => {
      if (checked) {
        return [...prev, fileId];
      } else {
        return prev.filter(id => id !== fileId);
      }
    });
  };

  // Handle select all files
  const handleSelectAllFiles = (checked: boolean) => {
    if (checked && tenant?.files) {
      const activeFileIds = tenant.files.filter(f => f.active).map(f => f.id);
      setSelectedFiles(activeFileIds);
    } else {
      setSelectedFiles([]);
    }
  };

  // Handle delete files
  const handleDeleteFiles = async (fileIds: string[]) => {
    if (!tenantId || fileIds.length === 0) return;

    setDeletingFiles(true);
    try {
      const response = await DataService.post(`api/Tenant/delete-files/${tenantId}`, fileIds);

      if (response.ok) {
        dispatch(showAlert({
          message: `Successfully deleted ${fileIds.length} file${fileIds.length > 1 ? 's' : ''}!`,
          type: 'success'
        }));

        // Clear selected files
        setSelectedFiles([]);

        // Re-fetch tenant data to get updated files
        const fetchResponse = await DataService.get(`api/Tenant/get/${tenantId}`);
        if (fetchResponse.ok) {
          const responseData = await fetchResponse.json();
          let tenantData: Tenant;
          if (responseData.success && responseData.data) {
            tenantData = responseData.data;
          } else if (responseData.id) {
            tenantData = responseData;
          } else {
            throw new Error('Invalid response structure');
          }
          setTenant(tenantData);
        }

        if (onRefresh) {
          onRefresh();
        }
      } else {
        let errorMessage = 'Failed to delete files';
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to delete files: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting files:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete files. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setDeletingFiles(false);
    }
  };

  // Handle delete single file
  const handleDeleteSingleFile = (fileId: string) => {
    handleDeleteFiles([fileId]);
  };

  // Handle delete selected files
  const handleDeleteSelectedFiles = () => {
    if (selectedFiles.length > 0) {
      handleDeleteFiles(selectedFiles);
    }
  };

  // Handle file upload success
  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    // Re-fetch tenant data to get updated files
    if (tenantId && open) {
      const fetchTenant = async () => {
        try {
          const response = await DataService.get(`api/Tenant/get/${tenantId}`);
          if (response.ok) {
            const responseData = await response.json();
            let tenantData: Tenant;
            if (responseData.success && responseData.data) {
              tenantData = responseData.data;
            } else if (responseData.id) {
              tenantData = responseData;
            } else {
              throw new Error('Invalid response structure');
            }
            setTenant(tenantData);
          }
        } catch (error) {
          console.error('Error re-fetching tenant:', error);
        }
      };
      fetchTenant();
    }
    // Clear selected files after upload
    setSelectedFiles([]);
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle edit success
  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    // Re-fetch tenant data to get updated information
    if (tenantId && open) {
      const fetchTenant = async () => {
        try {
          const response = await DataService.get(`api/Tenant/get/${tenantId}`);
          if (response.ok) {
            const responseData = await response.json();
            let tenantData: Tenant;
            if (responseData.success && responseData.data) {
              tenantData = responseData.data;
            } else if (responseData.id) {
              tenantData = responseData;
            } else {
              throw new Error('Invalid response structure');
            }
            setTenant(tenantData);
          }
        } catch (error) {
          console.error('Error re-fetching tenant:', error);
        }
      };
      fetchTenant();
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={56} />
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                Loading Tenant Details...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please wait while we fetch the tenant information
              </Typography>
            </Box>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Alert severity="error" sx={{ flex: 1 }}>
              {error}
            </Alert>
          </Box>
        ) : tenant ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                fontSize: '1.25rem'
              }}
            >
              {initials || <PersonIcon />}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                {tenant.fullName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  icon={tenant.active ? <CheckCircleIcon /> : <CancelIcon />}
                  label={tenant.active ? 'Active' : 'Inactive'}
                  color={tenant.active ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  ID: {tenant.id}
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Edit Tenant">
              <IconButton
                onClick={() => setEditDialogOpen(true)}
                color="primary"
                size="large"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : tenant ? (
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="primary" />
                  Contact Information
                </Typography>

                {/* Email Addresses */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Email Addresses
                  </Typography>
                  {tenant.emails.length > 0 ? (
                    <List dense>
                      {tenant.emails.map((email, index) => (
                        <ListItem key={index} sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <EmailIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={email}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2 }}>
                      No email addresses provided
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Phone Numbers */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Phone Numbers
                  </Typography>
                  {tenant.phoneNumbers.length > 0 ? (
                    <List dense>
                      {tenant.phoneNumbers.map((phone, index) => (
                        <ListItem key={index} sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <PhoneIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={phone}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2 }}>
                      No phone numbers provided
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Personal Information
                </Typography>

                {/* Nationality */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Nationality
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                    <PublicIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {tenant.nationality || (
                        <span style={{ fontStyle: 'italic', color: 'gray' }}>
                          Not specified
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Identification Type */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Identification Type
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {tenant.identificationType || (
                        <span style={{ fontStyle: 'italic', color: 'gray' }}>
                          Not specified
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Account Status */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tenant.active ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                  Account Status
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2 }}>
                  <Chip
                    icon={tenant.active ? <CheckCircleIcon /> : <CancelIcon />}
                    label={tenant.active ? 'Active Account' : 'Inactive Account'}
                    color={tenant.active ? 'success' : 'error'}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {tenant.active
                      ? 'This tenant account is currently active and can access services'
                      : 'This tenant account is inactive and cannot access services'
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Leases Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="primary" />
                  Tenant Leases ({leases.length})
                </Typography>

                {leasesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                  </Box>
                ) : leases.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <DescriptionIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No leases found for this tenant
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {leases.map((lease) => {
                      const statusInfo = getStatusInfo(lease.status);
                      const daysRemaining = getDaysRemaining(lease.endDate);

                      return (
                        <Grid item xs={12} md={6} key={lease.id}>
                          <StyledCard>
                            {/* Lease Header */}
                            <LeaseHeader>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="subtitle1" fontWeight="bold" fontSize="0.95rem">
                                  Lease #{lease.id.substring(0, 8)}
                                </Typography>
                                <Chip
                                  label={statusInfo.label}
                                  color={statusInfo.color}
                                  size="small"
                                  sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                              {lease.refNumber && (
                                <Typography variant="caption" fontSize="0.7rem">
                                  Ref: {lease.refNumber}
                                </Typography>
                              )}
                            </LeaseHeader>

                            <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                              {/* Lease Period */}
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="primary" fontWeight="600" fontSize="0.75rem" display="block" mb={0.5}>
                                  Lease Period
                                </Typography>
                                <Stack spacing={0.5}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 14 }} color="action" />
                                    <Typography variant="caption" fontSize="0.75rem">
                                      <strong>Start:</strong> {formatDate(lease.startDate)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 14 }} color="action" />
                                    <Typography variant="caption" fontSize="0.75rem">
                                      <strong>End:</strong> {formatDate(lease.endDate)}
                                    </Typography>
                                  </Box>
                                  {daysRemaining > 0 && (
                                    <Chip
                                      label={`${daysRemaining} days`}
                                      size="small"
                                      color={daysRemaining < 30 ? 'warning' : 'info'}
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                                    />
                                  )}
                                </Stack>
                              </Box>

                              <Divider sx={{ my: 1.5 }} />

                              {/* Lease Details */}
                              <Box sx={{ mb: 1.5 }}>
                                <Grid container spacing={0.5}>
                                  <Grid item xs={6}>
                                    <Box sx={{ bgcolor: 'grey.50', p: 0.75, borderRadius: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Type
                                      </Typography>
                                      <Typography variant="caption" fontWeight="600" fontSize="0.75rem" display="block">
                                        {lease.leaseType.toUpperCase()}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Box sx={{ bgcolor: 'grey.50', p: 0.75, borderRadius: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Currency
                                      </Typography>
                                      <Typography variant="caption" fontWeight="600" fontSize="0.75rem" display="block">
                                        {lease.currency}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>

                              <Divider sx={{ my: 1.5 }} />

                              {/* Unit Information */}
                              <Box>
                                <Typography variant="caption" color="primary" fontWeight="600" fontSize="0.75rem" display="block" mb={0.5}>
                                  Unit
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                  <HomeIcon sx={{ fontSize: 14 }} color="primary" />
                                  <Typography variant="caption" fontWeight="600" fontSize="0.8rem">
                                    Unit {lease.unit.number}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                                    • Floor {lease.unit.floor}
                                  </Typography>
                                </Box>
                                <Grid container spacing={0.5}>
                                  <Grid item xs={4}>
                                    <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 0.5, borderRadius: 1 }}>
                                      <MeetingRoomIcon sx={{ fontSize: 16 }} color="primary" />
                                      <Typography variant="caption" fontWeight="600" fontSize="0.8rem" display="block">
                                        {lease.unit.rooms}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Rooms
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 0.5, borderRadius: 1 }}>
                                      <BathtubIcon sx={{ fontSize: 16 }} color="primary" />
                                      <Typography variant="caption" fontWeight="600" fontSize="0.8rem" display="block">
                                        {lease.unit.bathrooms}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Baths
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 0.5, borderRadius: 1 }}>
                                      <SquareFootIcon sx={{ fontSize: 16 }} color="primary" />
                                      <Typography variant="caption" fontWeight="600" fontSize="0.8rem" display="block">
                                        {lease.unit.areaM2}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        m²
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>

                              {/* Fees Badge */}
                              {lease.fees && lease.fees.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Chip
                                    icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                                    label={`${lease.fees.length} Fee${lease.fees.length !== 1 ? 's' : ''}`}
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                </Box>
                              )}

                              {/* View Details Button */}
                              <Box sx={{ mt: 1.5 }}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    // Navigate to lease details - you can implement this based on your routing
                                    window.location.href = `/leases?leaseId=${lease.id}`;
                                  }}
                                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                                >
                                  View Lease Details
                                </Button>
                              </Box>
                            </CardContent>
                          </StyledCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Documents/Files */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon color="primary" />
                    Documents & Files
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedFiles.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={deletingFiles ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
                        onClick={handleDeleteSelectedFiles}
                        disabled={deletingFiles}
                        color="error"
                      >
                        {deletingFiles ? 'Deleting...' : `Delete Selected (${selectedFiles.length})`}
                      </Button>
                    )}
                    {tenant.files && tenant.files.filter(f => f.active).length > 1 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={downloadingAll ? <CircularProgress size={16} /> : <GetAppIcon />}
                        onClick={handleDownloadAllFiles}
                        disabled={downloadingAll}
                        color="secondary"
                      >
                        {downloadingAll ? 'Downloading...' : 'Download All'}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setUploadDialogOpen(true)}
                    >
                      Add Files
                    </Button>
                  </Box>
                </Box>

                {tenant.files && tenant.files.length > 0 ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 2, pr: 2, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {tenant.files.filter(f => f.active).length} active file{tenant.files.filter(f => f.active).length !== 1 ? 's' : ''} attached
                        {tenant.files.filter(f => !f.active).length > 0 && (
                          <span> ({tenant.files.filter(f => !f.active).length} inactive)</span>
                        )}
                      </Typography>
                      {tenant.files.filter(f => f.active).length > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            size="small"
                            checked={selectedFiles.length === tenant.files.filter(f => f.active).length}
                            indeterminate={selectedFiles.length > 0 && selectedFiles.length < tenant.files.filter(f => f.active).length}
                            onChange={(e) => handleSelectAllFiles(e.target.checked)}
                            disabled={deletingFiles}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Select All
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <List dense>
                      {tenant.files.filter(f => f.active).map((file, index) => {
                        const fileName = getFileNameFromUrl(file.fileUrl);
                        const extension = getFileExtension(file.fileUrl);
                        return (
                          <ListItem
                            key={file.id || index}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                              <Checkbox
                                size="small"
                                checked={selectedFiles.includes(file.id)}
                                onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                                disabled={deletingFiles}
                              />
                            </Box>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {getFileIcon(file.fileUrl)}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {fileName}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Type: {extension ? extension.toUpperCase() : 'Unknown'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                    ID: {file.id}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleFileDownload(file)}
                              >
                                Download
                              </Button>
                              <Tooltip title="Delete File">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSingleFile(file.id)}
                                  disabled={deletingFiles}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <AttachFileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No documents or files attached
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Files uploaded during tenant registration will appear here
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Close
        </Button>
      </DialogActions>

      {/* File Upload Dialog */}
      {tenant && (
        <TenantFileUploadDialog
          open={uploadDialogOpen}
          tenantId={tenant.id}
          tenantName={tenant.fullName}
          onClose={() => setUploadDialogOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Tenant Edit Dialog */}
      {tenant && (
        <TenantEditDialog
          open={editDialogOpen}
          tenant={tenant}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </Dialog>
  );
};

export default TenantDetailsDialog;